import React, { useState, useEffect, useMemo } from "react";
import {
    IClient,
    IArchivedClient,
    DateFormats,
    ClientStatus,
} from "intake_shared/src/types";
import moment from "../../../../../utils/app_moment";
import { RadioInput } from "../../../bits/RadioInput/RadioInput";
import { Select } from "../../../bits/Select/Select";
import { Dialog } from "../../../bits/Dialog/Dialog";
import styles from "./CompareMergeDialog.module.scss";
import Alert from "../../../bits/Alert/Alert";
import ShortenedText from "../../../bits/ShortenedText/ShortenedText";
import { cn, removeEnumUnderscore } from "../../../../helpers";
import { Loader } from "../../../bits/Loader/Loader";

interface FieldComparison {
    field: string;
    label: string;
    primaryValue: any;
    otherValue: any;
    selectedSource: "primary" | "other";
}

interface CompareMergeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onMerge: (
        primaryClientId: string,
        selectedFields: { [clientId: string]: string[] }
    ) => void;
    selectedClients: (IClient | IArchivedClient)[];
    loading?: boolean;
    defaultPrimaryClient?: IClient | IArchivedClient;
}

const FEE_FIELDS = [
    "masters_fee",
    "dr_fee",
    "psychiatry_fee",
    "career_fee",
    "gifted_assessment_fee",
    "short_term_evaluation_documentation_fee",
    "intern_fee",
    "extern_fee",
    "nutrition_fee",
    "lic_fee",
    "kap_fee",
    "lic_a_fee",
    "lic_b_fee",
    "lic_c_fee",
    "dr_fee_a",
    "dr_fee_b",
    "dr_fee_c",
    "psychiatry_fee_a",
    "psychiatry_fee_b",
    "psychiatry_fee_c",
    "inn_masters_fee",
    "inn_nutrition_fee",
    "testing_fee",
    "intern_extern_testing_fee",
    "is_self_pay",
];

export default function CompareMergeDialog({
    isOpen,
    onClose,
    onMerge,
    selectedClients,
    loading = false,
    defaultPrimaryClient,
}: CompareMergeDialogProps) {
    const [primaryClient, setPrimaryClient] = useState<
        IClient | IArchivedClient | null
    >(defaultPrimaryClient as IClient | IArchivedClient);
    const [fieldComparisons, setFieldComparisons] = useState<FieldComparison[]>([]);
    const [fieldSelections, setFieldSelections] = useState<Record<string, string>>(
        {}
    );
    const [isGeneratingComparisons, setIsGeneratingComparisons] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setPrimaryClient(null);
            setFieldComparisons([]);
            setFieldSelections({});
            setIsGeneratingComparisons(false);
        } else {
            if (defaultPrimaryClient && !primaryClient) {
                setPrimaryClient(defaultPrimaryClient);
            }
        }
    }, [isOpen, defaultPrimaryClient, primaryClient]);

    const otherClientsWithIds = useMemo(() => {
        if (!primaryClient) return [];
        return selectedClients
            .filter((client) => client.id !== primaryClient.id)
            .sort((a, b) => a.id.localeCompare(b.id))
            .map((client, index) => ({
                ...client,
                displayId: `Client ${String.fromCharCode(66 + index)}`,
                shortId: String.fromCharCode(66 + index),
            }));
    }, [primaryClient, selectedClients]);

    useEffect(() => {
        if (!primaryClient || selectedClients.length <= 1) {
            setFieldComparisons([]);
            setFieldSelections({});
            setIsGeneratingComparisons(false);
            return;
        }

        setIsGeneratingComparisons(true);

        const generateComparisons = async () => {
            const fieldsToCompare = [
                {
                    field: "preferred_name",
                    label: "Preferred Name",
                },
                {
                    field: "pronouns",
                    label: "Pronouns",
                },
                {
                    field: "phone_no",
                    label: "Phone Number",
                },
                { field: "address", label: "Address" },
                { field: "city", label: "City" },
                {
                    field: "zip_code",
                    label: "Zip Code",
                },
                { field: "requested_service", label: "Requested Service" },
                {
                    field: "insurance_provider",
                    label: "Insurance Provider",
                },
                {
                    field: "insurance_member_id",
                    label: "Insurance Member ID",
                },
                {
                    field: "insurance_plan_employer",
                    label: "Insurance Plan Employer",
                },
                {
                    field: "insurance_type",
                    label: "Insurance Type",
                },
                {
                    field: "accept_assignment",
                    label: "Accept Assignment",
                },
                {
                    field: ClientStatus.FEE_SETTING,
                    label: "Fee Setting",
                },
                {
                    field: "assigned_to",
                    label: "Assigned To",
                },
            ];

            const comparisons: FieldComparison[] = [];
            const initialSelections: Record<string, string> = {};

            fieldsToCompare.forEach(({ field, label }) => {
                const primaryValue =
                    primaryClient[field as keyof (IClient | IArchivedClient)];

                comparisons.push({
                    field,
                    label,
                    primaryValue,
                    otherValue: "Multiple values",
                    selectedSource: "primary",
                });

                initialSelections[field] = primaryClient.id;
            });

            await new Promise((resolve) => setTimeout(resolve, 300));

            setFieldComparisons(comparisons);
            setFieldSelections(initialSelections);
            setIsGeneratingComparisons(false);
        };

        generateComparisons();
    }, [primaryClient, selectedClients, otherClientsWithIds]);

    const handlePrimaryClientChange = (selectedOption: any) => {
        const client = selectedClients.find((c) => c.id === selectedOption.value);
        setPrimaryClient(client || null);
    };

    const handleFieldSelection = (field: string, clientId: string) => {
        setFieldSelections((prev) => ({
            ...prev,
            [field]: clientId,
        }));
    };

    const handleMerge = () => {
        const selectedFields: { [clientId: string]: string[] } = {};

        Object.entries(fieldSelections).forEach(([field, clientId]) => {
            if (!selectedFields[clientId]) {
                selectedFields[clientId] = [];
            }
            selectedFields[clientId].push(field);
        });

        if (!primaryClient) return;
        onMerge(primaryClient.id, selectedFields);
    };

    const primaryClientOptions = selectedClients.map((client) => ({
        value: client.id,
        label: `${client.first_name} ${client.last_name} | DOB: ${
            client.date_of_birth !== null
                ? moment(client?.date_of_birth).format(
                      DateFormats.DAY_MONTH_YEAR_FORMAT
                  )
                : "N/A"
        } | Status: ${client.status}`,
    }));

    const formatValue = (value: any): React.ReactNode | string => {
        if (value === "") return "N/A";
        if (value instanceof Date)
            return moment(value).format(DateFormats.DAY_MONTH_YEAR_FORMAT);
        if (typeof value === "object") {
            if (Array.isArray(value)) return `${value.length} items`;
            if (value && typeof value === "object") {
                const obj = value as Record<string, unknown>;
                if (FEE_FIELDS.some((field) => obj[field])) {
                    const fees: string[] = [];

                    if (obj.masters_fee) fees.push(`Masters: ${obj.masters_fee}`);
                    if (obj.dr_fee) fees.push(`Dr: ${obj.dr_fee}`);
                    if (obj.psychiatry_fee)
                        fees.push(`Psych: ${obj.psychiatry_fee}`);

                    if (obj.career_fee) fees.push(`Career: ${obj.career_fee}`);
                    if (obj.gifted_assessment_fee) {
                        fees.push(`Gifted Assessment: ${obj.gifted_assessment_fee}`);
                    }
                    if (obj.short_term_evaluation_documentation_fee) {
                        fees.push(
                            `Short Term Eval & Documentation: ${obj.short_term_evaluation_documentation_fee}`
                        );
                    }
                    if (obj.intern_fee) fees.push(`Intern: ${obj.intern_fee}`);
                    if (obj.extern_fee) fees.push(`Extern: ${obj.extern_fee}`);
                    if (obj.nutrition_fee)
                        fees.push(`Nutrition: ${obj.nutrition_fee}`);

                    if (obj.lic_fee) fees.push(`LIC: ${obj.lic_fee}`);
                    if (obj.kap_fee) fees.push(`Kap: ${obj.kap_fee}`);
                    if (obj.lic_a_fee) fees.push(`LIC A: ${obj.lic_a_fee}`);
                    if (obj.lic_b_fee) fees.push(`LIC B: ${obj.lic_b_fee}`);
                    if (obj.lic_c_fee) fees.push(`LIC C: ${obj.lic_c_fee}`);

                    if (obj.dr_fee_a) fees.push(`Dr A: ${obj.dr_fee_a}`);
                    if (obj.dr_fee_b) fees.push(`Dr B: ${obj.dr_fee_b}`);
                    if (obj.dr_fee_c) fees.push(`Dr C: ${obj.dr_fee_c}`);

                    if (obj.psychiatry_fee_a)
                        fees.push(`Psych A: ${obj.psychiatry_fee_a}`);
                    if (obj.psychiatry_fee_b)
                        fees.push(`Psych B: ${obj.psychiatry_fee_b}`);
                    if (obj.psychiatry_fee_c)
                        fees.push(`Psych C: ${obj.psychiatry_fee_c}`);

                    if (obj.inn_masters_fee)
                        fees.push(`INN Masters: ${obj.inn_masters_fee}`);
                    if (obj.inn_nutrition_fee)
                        fees.push(`INN Nutrition: ${obj.inn_nutrition_fee}`);

                    if (obj.testing_fee) fees.push(`Testing: ${obj.testing_fee}`);
                    if (obj.intern_extern_testing_fee)
                        fees.push(
                            `Intern/Extern Testing: ${obj.intern_extern_testing_fee}`
                        );

                    if (obj.is_self_pay) fees.push(`Self-Pay: Yes`);

                    return fees.length > 0 ? (
                        <ShortenedText
                            notes={fees.join(", ")}
                            identifyingKey={`fees-${obj.id}`}
                        />
                    ) : (
                        "N/A"
                    );
                }
            }
            return "N/A";
        }
        if (typeof value === "boolean") return value ? "Yes" : "No";
        return String(removeEnumUnderscore(value));
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title="Compare & Merge Clients"
            onCancel={onClose}
            onSave={handleMerge}
            cancelText="Cancel"
            saveText={loading ? "Merging..." : "Merge Clients"}
            isSubmitBtnDisabled={!primaryClient || loading}
            wrapperClassName={styles.compareMergeDialog}
        >
            <div className={styles.content}>
                <div className={styles.primaryClientSelection}>
                    <Select
                        options={primaryClientOptions}
                        onChange={handlePrimaryClientChange}
                        placeholder="Select a primary client..."
                        isSearchable={false}
                        label="Choose the primary client. This client's details will be kept, and
                        other selected clients' fields will be merged into it."
                        isRequired
                        value={primaryClientOptions.find(
                            (option) => option.value === primaryClient?.id
                        )}
                        isLongListInDialog
                        defaultValue={primaryClientOptions.find(
                            (option) => option.value === defaultPrimaryClient?.id
                        )}
                    />
                </div>

                {primaryClient && (
                    <>
                        <div className={styles.clientInfo}>
                            <div className={styles.primaryClient}>
                                <span className={styles.primaryClientLabel}>
                                    Primary Client(Client A):
                                </span>
                                <span>
                                    {primaryClient.first_name}{" "}
                                    {primaryClient.last_name} | DOB:
                                    {primaryClient.date_of_birth !== null
                                        ? moment(primaryClient.date_of_birth).format(
                                              DateFormats.DAY_MONTH_YEAR_FORMAT
                                          )
                                        : "N/A"}{" "}
                                    | Status: {primaryClient.status}
                                </span>
                            </div>
                            <div className={styles.otherClients}>
                                <span className={styles.otherClientsLabel}>
                                    Other Clients ({otherClientsWithIds.length})
                                </span>
                                {otherClientsWithIds.map((client) => (
                                    <div
                                        key={client.id}
                                        className={styles.otherClient}
                                    >
                                        <span>
                                            <strong>{client.displayId}:</strong>{" "}
                                            {client.first_name} {client.last_name} |
                                            DOB:{" "}
                                            {client.date_of_birth !== null
                                                ? moment(
                                                      client.date_of_birth
                                                  ).format(
                                                      DateFormats.DAY_MONTH_YEAR_FORMAT
                                                  )
                                                : "N/A"}{" "}
                                            | Status: {client.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.fieldsContainer}>
                            <Loader
                                show={isGeneratingComparisons}
                                loadingText="Generating field comparisons..."
                            />

                            <Alert type="info">
                                The primary client's details will be kept for all
                                fields unless you choose a different client's details
                                below. You can select only one client per field.
                                Notes and booking details are automatically combined
                                from all selected clients.
                            </Alert>

                            <div className={styles.fieldsList}>
                                {fieldComparisons.map((comp) => (
                                    <div
                                        key={comp.field}
                                        className={styles.fieldRow}
                                    >
                                        <label className={styles.fieldLabel}>
                                            {comp.label}
                                        </label>

                                        <div
                                            className={cn(styles.valueOption, {
                                                [styles.multipleValues]:
                                                    otherClientsWithIds.length > 1,
                                            })}
                                        >
                                            <RadioInput
                                                checked={
                                                    fieldSelections[comp.field] ===
                                                    primaryClient.id
                                                }
                                                onChange={() =>
                                                    handleFieldSelection(
                                                        comp.field,
                                                        primaryClient.id
                                                    )
                                                }
                                                name={`field-${comp.field}`}
                                                value={primaryClient.id}
                                                label={
                                                    <span>
                                                        <span
                                                            className={
                                                                styles.labelText
                                                            }
                                                        >
                                                            Primary:
                                                        </span>{" "}
                                                        <span>
                                                            {formatValue(
                                                                comp.primaryValue
                                                            )}
                                                        </span>
                                                    </span>
                                                }
                                            />

                                            {otherClientsWithIds.map(
                                                (otherClient) => {
                                                    const otherValue =
                                                        otherClient[
                                                            comp.field as keyof (
                                                                | IClient
                                                                | IArchivedClient
                                                            )
                                                        ];
                                                    const isSelected =
                                                        fieldSelections[
                                                            comp.field
                                                        ] === otherClient.id;

                                                    return (
                                                        <div key={otherClient.id}>
                                                            <RadioInput
                                                                checked={isSelected}
                                                                onChange={() =>
                                                                    handleFieldSelection(
                                                                        comp.field,
                                                                        otherClient.id
                                                                    )
                                                                }
                                                                name={`field-${comp.field}`}
                                                                value={
                                                                    otherClient.id
                                                                }
                                                                label={
                                                                    <span>
                                                                        <span
                                                                            className={
                                                                                styles.labelText
                                                                            }
                                                                        >
                                                                            {
                                                                                otherClient.displayId
                                                                            }
                                                                            :
                                                                        </span>{" "}
                                                                        <span>
                                                                            {formatValue(
                                                                                otherValue
                                                                            )}
                                                                        </span>
                                                                    </span>
                                                                }
                                                            />
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
}
